import { registerSW } from 'virtual:pwa-register';

import { downloadBlob } from './download';
import { HandwriterError } from './errors';
import { buildNoteFile, generateFilename } from './frontmatter';
import { transcribe } from './ocr';
import type { Actions, AppState, Screen } from './state';
import { loadSettings, saveSettings } from './storage';
import { renderCapture } from './ui/capture';
import { renderReview } from './ui/review';
import { renderSettings } from './ui/settings';
import { renderSuccess } from './ui/success';
import { uploadNote } from './worker-client';

import './style.css';

registerSW({
  immediate: true,
  onRegisteredSW(_swUrl, registration) {
    if (!registration) return;
    // Foregrounding the installed PWA should actively check for an
    // update — Safari's standalone mode is unreliable about doing this
    // on its own.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        registration.update().catch(() => {
          /* non-fatal — try again next time */
        });
      }
    });
  },
});

const root = document.getElementById('app');
if (!root) throw new Error('#app element missing');

const settings = loadSettings();

const state: AppState = {
  screen: 'capture',
  settings,
  selectedMachine: settings.machines.find((m) => m.name.length > 0)?.name ?? '',
  pendingImage: null,
  transcription: null,
  editingTitle: '',
  editingTagsText: '',
  editingBook: '',
  isTranscribing: false,
  isUploading: false,
  errorMessage: null,
  lastUploadedFilename: null,
};

const actions: Actions = {
  go(screen: Screen) {
    state.screen = screen;
    state.errorMessage = null;
    render();
  },
  setSelectedMachine(name: string) {
    state.selectedMachine = name;
  },
  async startTranscription(image) {
    state.pendingImage = image;
    state.isTranscribing = true;
    state.errorMessage = null;
    render();
    try {
      const result = await transcribe(image.base64, image.mimeType, state.settings.apiKey, state.settings.provider);
      state.transcription = result;
      state.editingTitle = result.title;
      state.editingTagsText = result.tags.join(', ');
      state.editingBook = state.settings.books.find((b) => b.length > 0) ?? '';
      state.screen = 'review';
    } catch (err) {
      state.errorMessage = userMessage(err);
      // Keep pendingImage so the user can retry without re-picking.
    } finally {
      state.isTranscribing = false;
      render();
    }
  },
  async confirmUpload() {
    if (!state.transcription) {
      state.errorMessage = 'No transcription to send.';
      render();
      return;
    }

    const now = new Date().toISOString();
    const title = state.editingTitle.trim() || 'Untitled';
    const filename = generateFilename(title, now);

    state.isUploading = true;
    state.errorMessage = null;
    render();
    try {
      if (state.settings.deliveryMode === 'download') {
        const body = `${state.transcription.body.trimEnd()}\n`;
        downloadBlob(new Blob([body], { type: 'text/markdown' }), filename);
      } else {
        const machine = state.settings.machines.find((m) => m.name === state.selectedMachine);
        if (!machine) {
          state.errorMessage = 'Pick a machine before sending.';
          return;
        }
        const tags = state.editingTagsText
          .split(',')
          .map((t) => t.trim())
          .filter((t) => t.length > 0);
        const book = state.editingBook || state.settings.books[0] || '';
        const content = buildNoteFile(
          {
            uuid: crypto.randomUUID(),
            title,
            dateCreated: now,
            dateUpdated: now,
            tags,
            type: 'handwritten',
            book,
          },
          state.transcription.body,
        );
        await uploadNote({
          workerUrl: machine.workerUrl,
          bearerToken: machine.bearerToken,
          filename,
          content,
        });
      }
      state.lastUploadedFilename = filename;
      state.screen = 'success';
      state.transcription = null;
      state.pendingImage = null;
    } catch (err) {
      state.errorMessage = userMessage(err);
    } finally {
      state.isUploading = false;
      render();
    }
  },
  retake() {
    state.transcription = null;
    state.pendingImage = null;
    state.screen = 'capture';
    state.errorMessage = null;
    render();
  },
  updateSettings(next) {
    state.settings = next;
    saveSettings(next);
    const stillValid = next.machines.some(
      (m) => m.name.length > 0 && m.name === state.selectedMachine,
    );
    if (!stillValid) {
      state.selectedMachine = next.machines.find((m) => m.name.length > 0)?.name ?? '';
    }
    // No re-render — callers do that explicitly on structural changes
    // (add/remove rows) so live text edits don't steal input focus.
  },
  rerender: render,
};

function render(): void {
  if (!root) return;
  switch (state.screen) {
    case 'settings':
      renderSettings(root, state, actions);
      break;
    case 'capture':
      renderCapture(root, state, actions);
      break;
    case 'review':
      renderReview(root, state, actions);
      break;
    case 'success':
      renderSuccess(root, state, actions);
      break;
  }
  maybeApplyUpdate();
}

// Reload when a new service worker takes over so the user sees the
// update without manually killing and relaunching the PWA. Deferred
// until the capture screen is idle so we never wipe a pending image,
// transcription, or upload.
let pendingUpdate = false;
if (navigator.serviceWorker?.controller) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    pendingUpdate = true;
    maybeApplyUpdate();
  });
}

function maybeApplyUpdate(): void {
  if (!pendingUpdate) return;
  if (state.isTranscribing || state.isUploading) return;
  if (state.screen !== 'capture') return;
  window.location.reload();
}

function userMessage(err: unknown): string {
  if (err instanceof HandwriterError) return err.userMessage;
  if (err instanceof Error) return err.message;
  return 'Something went wrong.';
}

render();
